import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

const crudGlobalAttributes = [
  PermissionAttributesEnum.GlobalOperationAutomationCreateany,
  PermissionAttributesEnum.GlobalOperationAutomationReadany,
  PermissionAttributesEnum.GlobalOperationAutomationUpdateany,
  PermissionAttributesEnum.GlobalOperationAutomationDeleteany,
];

const crudContextAttributes = [
  PermissionAttributesEnum.ContextOperationAutomationCreate,
  PermissionAttributesEnum.ContextOperationAutomationRead,
  PermissionAttributesEnum.ContextOperationAutomationUpdate,
  PermissionAttributesEnum.ContextOperationAutomationDelete,
];

/** Mirrors the API AutomationVoter and the AutomationExtension that filters the automation lists. */
export class AutomationVoter extends ContextVoter implements ContextVoterInterface {
  /**
   * Mirrors the API AutomationVoter CREATE on POST /automations: the global create_any right, or the context
   * create right on the organisation or the location of the automation.
   */
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationAutomationCreateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationAutomationCreate,
    });
  };

  /** Mirrors the API AutomationVoter READ on GET /automations/{id}: read_any, or the context read right on the automation's location or organisation. */
  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this.canOnAutomation(
      item,
      PermissionAttributesEnum.GlobalOperationAutomationReadany,
      PermissionAttributesEnum.ContextOperationAutomationRead,
    );
  };

  /**
   * Mirrors the API AutomationVoter UPDATE: on an automation, update_any or the context update right on its location
   * or organisation; without one (the bulk PATCH /automations/bulk), update_any or the context update right anywhere.
   */
  public canUpdate: ContextVoterInterface["canUpdate"] = (item) => {
    if (item === undefined) {
      return this.canAnywhere(
        PermissionAttributesEnum.GlobalOperationAutomationUpdateany,
        PermissionAttributesEnum.ContextOperationAutomationUpdate,
      );
    }

    return this.canOnAutomation(
      item,
      PermissionAttributesEnum.GlobalOperationAutomationUpdateany,
      PermissionAttributesEnum.ContextOperationAutomationUpdate,
    );
  };

  /**
   * Mirrors the API AutomationVoter DELETE: on an automation, delete_any or the context delete right on its location
   * or organisation; without one (the bulk DELETE /automations/bulk), delete_any or the context delete right anywhere.
   */
  public canDelete: ContextVoterInterface["canDelete"] = (item) => {
    if (item === undefined) {
      return this.canAnywhere(
        PermissionAttributesEnum.GlobalOperationAutomationDeleteany,
        PermissionAttributesEnum.ContextOperationAutomationDelete,
      );
    }

    return this.canOnAutomation(
      item,
      PermissionAttributesEnum.GlobalOperationAutomationDeleteany,
      PermissionAttributesEnum.ContextOperationAutomationDelete,
    );
  };

  /**
   * Whether the automation lists of the chosen location or organisation hold anything the user may work with: a global
   * automation right, or a context automation right that the API AutomationExtension lets through for that scope.
   * A location is reached by a right on the location or on its organisation. An organisation lists its own automations
   * and those of its locations, so a right on the organisation or on one of its locations counts.
   */
  public canCrud: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: crudGlobalAttributes }) !== null) {
      return true;
    }

    if (item.location) {
      return this.hasAccess({ location: item.location, client: item.client, attribute: crudContextAttributes });
    }

    if (!item.client) {
      return false;
    }

    if (this.permissionService.getClientPermissionForUser({ clientIri: item.client, attribute: crudContextAttributes }) !== null) {
      return true;
    }

    return this.permissionService.getPermissionsByScope({ scope: PermissionScope.LOCATION, attribute: crudContextAttributes })
      .some(permission => !!permission.location && this.permissionService.getClientViaLocation(permission.location) === item.client);
  };

  // The API checks the automation's own location and its own organisation, each on its own: a location without an
  // organisation on the automation is not widened to the location's organisation.
  private canOnAutomation(
    item: Omit<CrudArgs, "@id">,
    globalAttribute: PermissionAttributesEnum,
    contextAttribute: PermissionAttributesEnum,
  ): boolean {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: globalAttribute }) !== null) {
      return true;
    }

    if (item.location && this.permissionService.getLocationPermissionForUser({ locationIri: item.location, attribute: contextAttribute }) !== null) {
      return true;
    }

    return !!item.client && this.permissionService.getClientPermissionForUser({ clientIri: item.client, attribute: contextAttribute }) !== null;
  }

  // Mirrors UserPermissionService::hasAttributeAnywhere: any valid permission of the user, in any scope, that holds the right.
  private canAnywhere(globalAttribute: PermissionAttributesEnum, contextAttribute: PermissionAttributesEnum): boolean {
    if (this.permissionService.getGlobalPermissionForUser({ attribute: globalAttribute }) !== null) {
      return true;
    }

    return this.permissionService.getPermissionsByScopeLess({ attribute: contextAttribute }).length > 0;
  }
}
